import { css } from "emotion";
import { Component } from "preact";
import React from "react";
import CMSComponent from "../../components/CMSComponent";
import Button from "../../components/global/Button";
import H1 from "../../components/global/H1";
import H3 from "../../components/global/H3";
import H5 from "../../components/global/H5";
import P from "../../components/global/P";
import Image from "../../components/Image";
import Banner from "../../components/layout/Banner";
import ContentBlock from "../../components/layout/ContentBlock";
import Layout from "../../components/layout/Layout";
import { updateTitle } from "../../utils/BrowserUtils";
import { firstOfType, max } from "../../utils/CssUtils";
import DescriptionBlock from "./DescriptionBlock";
import ProjectCard from "./ProjectCard";
import Flag from "./Projects/componets/Flag";
import FlexColumn from "./Projects/componets/FlexColumn";
import FlexRow from "./Projects/componets/FlexRow";
import HowToHelp from "./Projects/componets/HowToHelp";

const bannerImage = require("../../assets/Projects/Our_projects_banner.png");
const supportHarvest = require("../../assets/Projects/we_aim_to_close.jpg");

const girlsLiteracy = require("../../assets/Projects/room2ReadCard.png");
const indigenousScholarship = require("../../assets/Projects/YalariBannerCard.jpg");
const schoolMeals = require("../../assets/Projects/ozHarvestCard.png");
const pasifikaLearners = require("../../assets/Projects/students-st-pius-school.png");
const KiwiNutrition = require("../../assets/Projects/nutrition-for-Kiwis.png");

const registeredCharity = require("../../assets/Projects/registered_charity.png");
const purityCharity = require("../../assets/Projects/pure-charity-logo.png");
const sustainableGoals = require("../../assets/Projects/un-sustainable-development-goals.png");

const icLiteracyPrograms = require("../../assets/ic-literacy-programs.svg");
const icNutrition = require("../../assets/ic-nutrition.svg");
const icScholarships = require("../../assets/ic-scholarships.svg");
const icComputer = require("../../assets/Projects/ic_maori&pasifika.svg");

const sustainableGoalsWrapper = css({
  textAlign: "center",
  maxWidth: 500,
  margin: "auto",
  [max(600)]: {
    minWidth: 292
  },
  "& div:first-of-type": {
    marginBottom: 16,
    padding: "5px 10px",
    maxWidth: 300,
    [max(600)]: {
      maxWidth: 260
    }
  },
  h3: {
    marginBottom: 16,
    maxWidth: 480,
    a: {
      color: "#196097",
      textDecoration: "underline",
      fontWeight: 600
    }
  }
});

const bannerContent = css({
  padding: "10px 0px 10px 80px",
  textAlign: "left",
  h1: {
    maxWidth: 410,
    marginBottom: 12,
    fontSize: 36
  },
  h5: {
    maxWidth: 410,
    marginBottom: 20,
    marginLeft: 0,
    fontSize: 16,
    fontWeight: "normal"
  },
  "@media(max-width: 1080px)": {
    padding: "112px 0px 24px 80px"
  },
  "@media(max-width: 768px)": {
    padding: "40px 14px 40px 14px",
    h1: {
      fontSize: 22
    },
    h5: {
      fontSize: 16
    }
  },
  "@media(max-width: 600px)": {
    padding: "40px 14px 48px 14px"
  }
});

const projectCardsWrapper = {
  flexWrap: "wrap",
  "&>div": {
    margin: 12.5,
    [max(600)]: {
      margin: "0 0 18px 0"
    }
  }
};

const projectCardContentBlock = css({
  padding: "59.5px 67.5px 59.5px 67.5px",
  [max(600)]: {
    padding: "0 14px 22px 14px"
  }
});

const bookingFeeWrapper = {
  marginBottom: 72,
  [firstOfType()]: {
    minWidth: 292
  },
  h3: {
    marginBottom: 16,
    [max(600)]: {
      marginBottom: 12
    }
  }
};

const bookingFeeImageWrapper = css({
  minWidth: 292,
  maxWidth: 480
});

const educationGapWrapper = css({
  "h3:first-of-type": {
    marginBottom: 48,
    textAlign: "center",
    [max(600)]: {
      marginBottom: 32
    }
  },
  [max(600)]: {
    padding: "0 14px",
    margin: "auto"
  }
});

const educationGapFlags = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexWrap: "wrap",
  margin: "-10px -10px 38px -10px",
  [max(810)]: {
    flexDirection: "column",
    marginBottom: 40
  }
});

const flagWrapper = css({
  maxWidth: 262,
  minHeight: 100,
  letterSpacing: "-0.37px",
  padding: "6px 0 6px 12px",
  margin: "10px 10px 10px 117px",
  [firstOfType()]: {
    margin: 10
  },
  [max(810)]: {
    marginTop: 55
  },
  [max(600)]: {
    padding: "12px 0 0 0",
    margin: "55px 25px 10px 25px",
    maxWidth: "100%",
    minHeight: 76,
    [firstOfType()]: {
      margin: "0 25px 10px 25px"
    }
  }
});

const educationGapButtonWrapper = css({
  textAlign: "center"
});

let ourProjects = {
  au: [
    {
      img: girlsLiteracy,
      alt:
        "Three girls learning to read in the girls literacy education program",
      icon: icLiteracyPrograms,
      title: "Girls’ Education Programs",
      textP1: "62 million adolescent girls around the world are out of school.",
      textP2: "We’re changing that, right around the world.",
      ourImpactP1: "52,000 days",
      ourImpactP2: "of education for girls",
      link: "/au/our-impact/room-to-read"
    },
    {
      img: indigenousScholarship,
      alt: "Charity gifting indigenous school students scholarships",
      icon: icScholarships,
      title: "Indigenous Scholarships",
      textP1: "Only 65% of Indigenous students complete year 12.",
      textP2: "We’re giving scholarships so that everyone has an equal chance.",
      ourImpactP1: "1,700 days",
      ourImpactP2: "of scholarships for indigenous students",
      link: "/au/our-impact/yalari"
    },
    {
      img: schoolMeals,
      alt: "Charity: OzHarvest, giving school students food",
      icon: icNutrition,
      title: "Nutrition for School Kids",
      textP1:
        "One child in every classroom in Australia goes to school hungry.",
      textP2:
        "We’re equipping kids with the ingredients and the food skills for good nutrition.",
      ourImpactP1: "150,000 meals",
      ourImpactP2: "for disadvantaged kids",
      link: "/au/our-impact/ozharvest"
    }
  ],
  nz: [
    {
      img: girlsLiteracy,
      alt:
        "Three girls learning to read in the girls literacy education program",
      icon: icLiteracyPrograms,
      title: "Girls’ Education Programs",
      textP1: "62 million adolescent girls around the world are out of school.",
      textP2: "We’re changing that, right around the world.",
      link: "/nz/our-impact/room-to-read"
    },
    {
      img: pasifikaLearners,
      alt: "Maori and Pasifika learners",
      icon: icComputer,
      title: "Māori and Pasifika Learners",
      textP1:
        "Aotearoa New Zealand’s education gap is one of the largest in the world.",
      textP2:
        "So we support Māori, Pasifika and disadvantaged learners to get equal access to digital education.",
      link: "/nz/our-impact/manaiakalani"
    },
    {
      img: KiwiNutrition,
      alt: "Nutrition for kiwi students",
      icon: icNutrition,
      title: "Nutrition for Kiwi School Kids",
      textP1:
        "Every day in New Zealand, one child in every classroom goes to school hungry.",
      textP2:
        "With an equal start, every child has the opportunity to reach their full potential.",
      link: "/nz/our-impact/kidscan"
    }
  ]
};

const flagTexts = [
  `The event platform is how we raise more money. 
  We choose to be self-sufficient, rather than asking for donations.`,
  `We cover costs, build new features and take on competitors. 
  For every investment we make, we ask “will this help us increase our impact?”`,
  `Our education projects are why we do what we do. When we grow, so does our impact. `
];

class OurImpact extends Component {
  constructor(props) {
    super(props);
  }

  componentDidMount() {
    updateTitle("Our Impact");
  }

  render() {
    const { localeData, contentData, locale } = this.props;

    if (contentData && contentData.data && contentData.data.projectCards) {
      ourProjects[locale] = [];

      const { projects, images, icons } = contentData.data.projectCards;

      let keys = Object.keys(projects);
      let project = {};
      for (const key of keys) {
        project = {
          img: images[keys.indexOf(key)].fields.file.url,
          alt: projects[key].alt,
          icon: icons[keys.indexOf(key)].fields.file.url,
          title: projects[key].title,
          textP1: projects[key].textP1,
          textP2: projects[key].textP2,
          link: projects[key].link
        };

        if (locale === "au") {
          Object.assign(project, {
            ourImpactP1: projects[key].ourImpactP1,
            ourImpactP2: projects[key].ourImpactP2
          });
        }

        ourProjects[locale].push(project);
      }
    }

    let impactPartners = [];

    if (contentData && contentData.data && contentData.data.ourImpactPartners) {
      let impactPartnersImages = contentData.data.ourImpactPartners.images;
      impactPartners = impactPartnersImages.map(image => {
        return { img: image.fields.file.url, alt: image.fields.description };
      });
    }

    let bannerImageObj = {
      img: bannerImage,
      alt: "Our Impact banner image"
    };

    if (contentData && contentData.data && contentData.data.banner) {
      bannerImageObj = {
        img: contentData.data.banner.image.fields.file.url,
        alt: contentData.data.banner.image.fields.description
      };
    }

    const charityImage = locale == "au" ? registeredCharity : purityCharity;

    return (
      <Layout>
        <Banner
          image={bannerImageObj.img}
          bgColor="greyBackground"
          alt="Kids in a classroom holding up their hands for the question"
        >
          <div className={bannerContent}>
            <H1>We believe education is a ticket to opportunity </H1>
            <H5>
              We exist to close the education gap around the world. To get
              there, we’re helping every event give opportunity to children that
              have been left behind. <br />
              <span style={{ fontWeight: "600" }}>
                We won’t stop until every ticket counts.
              </span>
            </H5>
          </div>
        </Banner>

        <DescriptionBlock
          heading="How we’re closing the education gap"
          image={supportHarvest}
          alt="Young child holding up her answer board in classroom"
          text={`
              Every child has the right to reach their potential and make a
              contribution to their community and the world. Fair and equal
              access to education is the biggest single step towards solving
              all the problems that keep humanity awake at night.
          `}
        />

        <div className={projectCardContentBlock}>
          <FlexRow style={projectCardsWrapper}>
            {ourProjects[locale].map(project => (
              <ProjectCard locale={locale} contentData={project} />
            ))}
          </FlexRow>
        </div>

        <ContentBlock color="grey">
          <FlexRow
            spacing={80}
            mobileSpacing={24}
            mobileStyles={{ marginBottom: 40 }}
            style={bookingFeeWrapper}
          >
            <div style={{ flexShrink: 2, minWidth: 215, maxWidth: 480 }}>
              <H3>
                How much of the booking fee goes to charity?{" "}
                <span style={{ color: "#41157a" }}>ALL OF IT.</span>
              </H3>
              <P>
                We get asked this a lot. We get it. It’s why we like everyone to
                know that Humanitix is a registered charity, not a for-profit
                business. We don’t have any shareholders to pay dividends to, or
                investors to pay back.
              </P>
            </div>
            <div className={bookingFeeImageWrapper}>
              <Image
                alt="Registered charity certificate"
                imageURL={charityImage}
              />
            </div>
          </FlexRow>

          <div className={educationGapWrapper}>
            <H3>Everything we do is about closing the education gap</H3>
            <div className={educationGapFlags}>
              {flagTexts.map((text, i) => (
                <Flag
                  showArrow={i == 0 ? false : true}
                  className={flagWrapper}
                  arrowSpacing={44}
                  arrowMobileSpacing={28}
                  custom={true}
                  breakpoint={810}
                >
                  <P>{text}</P>
                </Flag>
              ))}
            </div>
            <div className={educationGapButtonWrapper}>
              <Button size="large" link={`/${locale}/our-stories`}>
                See our story
              </Button>
            </div>
          </div>
        </ContentBlock>

        <ContentBlock color="#eeecf0">
          <FlexColumn className={sustainableGoalsWrapper}>
            <div>
              <Image
                alt="Sustainable development logo"
                imageURL={sustainableGoals}
              />
            </div>
            <H3>
              We’re aligned with the United Nations{" "}
              <a href="https://sustainabledevelopment.un.org/sdg4">
                Sustainable development goal 4
              </a>
            </H3>
            <P>
              Ensure inclusive and equitable quality education and promote
              lifelong learning opportunities for all.
            </P>
          </FlexColumn>
        </ContentBlock>

        <HowToHelp
          localeData={localeData}
          text="We’re a charity for the tech-generation. We don’t ask for donations. We ask anyone running an awesome event to use our platform so that your booking fees can change lives."
        />
      </Layout>
    );
  }
}

export default CMSComponent("projectCards", OurImpact, 500);
